/**
 * Nalatenschap Dossier Card
 *
 * Dashboard card showing the estate dossier with last update date.
 * Clicking navigates to the full dossier form for managing
 * contact details, information, and actions.
 *
 * @requirement Consumer Dashboard
 */

import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  Icon,
  Badge,
} from '@myvault/shared/components';

interface NalatenschapCardData {
  lastUpdatedAt: string | null;
  contactsCount: number;
  actionsCount: number;
  completedActions: number;
}

interface ApiNalatenschapSummary {
  last_updated_at: string | null;
  contacts_count: number;
  actions_count: number;
  completed_actions: number;
}

async function fetchNalatenschapSummary(): Promise<NalatenschapCardData> {
  const token = localStorage.getItem('myvault_access_token');
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch('/api/v1/nalatenschap/summary', {
    credentials: 'include',
    headers,
  });

  if (!response.ok) {
    // Return default empty state if endpoint doesn't exist yet
    if (response.status === 404) {
      return {
        lastUpdatedAt: null,
        contactsCount: 0,
        actionsCount: 0,
        completedActions: 0,
      };
    }
    throw new Error('Failed to fetch nalatenschap summary');
  }

  const data: ApiNalatenschapSummary = await response.json();

  return {
    lastUpdatedAt: data.last_updated_at,
    contactsCount: data.contacts_count,
    actionsCount: data.actions_count,
    completedActions: data.completed_actions,
  };
}

function formatLastUpdate(dateString: string | null): string {
  if (!dateString) {
    return 'Nog niet ingesteld';
  }

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffDays === 0) return 'Vandaag bijgewerkt';
  if (diffDays === 1) return 'Gisteren bijgewerkt';
  if (diffDays < 7) return `${diffDays} dagen geleden`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weken geleden`;

  return date.toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function NalatenschapDossierCard() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['nalatenschap-summary'],
    queryFn: fetchNalatenschapSummary,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false, // Don't retry if endpoint doesn't exist
  });

  // Default data when loading or no data
  const cardData: NalatenschapCardData = data || {
    lastUpdatedAt: null,
    contactsCount: 0,
    actionsCount: 0,
    completedActions: 0,
  };

  const hasSetup = cardData.lastUpdatedAt !== null;

  if (error) {
    // Show card with "not setup" state on error (endpoint might not exist)
    return (
      <NalatenschapCardContent
        data={{
          lastUpdatedAt: null,
          contactsCount: 0,
          actionsCount: 0,
          completedActions: 0,
        }}
        isLoading={false}
      />
    );
  }

  return <NalatenschapCardContent data={cardData} isLoading={isLoading} />;
}

interface NalatenschapCardContentProps {
  data: NalatenschapCardData;
  isLoading: boolean;
}

function NalatenschapCardContent({ data, isLoading }: NalatenschapCardContentProps) {
  const hasSetup = data.lastUpdatedAt !== null;

  return (
    <Link
      to="/nalatenschap"
      className="group block"
      aria-label="Ga naar nalatenschap dossier"
    >
      <Card className="border-l-4 border-l-purple-500 transition-all hover:border-purple-600 hover:shadow-md group-hover:bg-purple-50/50">
        <CardContent className="py-4">
          {isLoading ? (
            <div className="animate-pulse">
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 rounded-lg bg-gray-200" />
                <div className="flex-1 space-y-2">
                  <div className="h-5 w-32 rounded bg-gray-200" />
                  <div className="h-4 w-24 rounded bg-gray-200" />
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-4">
              {/* Icon */}
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-purple-100 group-hover:bg-purple-200">
                <Icon
                  name="heart"
                  variant="solid"
                  size="md"
                  className="text-purple-600"
                />
              </div>

              {/* Content */}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-gray-900 group-hover:text-purple-700">
                    Nalatenschap dossier
                  </h3>
                  {!hasSetup && (
                    <Badge variant="warning" size="sm">
                      Nieuw
                    </Badge>
                  )}
                </div>

                <p className="mt-1 text-sm text-gray-500">
                  {hasSetup
                    ? formatLastUpdate(data.lastUpdatedAt)
                    : 'Bereid je nabestaanden voor'}
                </p>

                {hasSetup && data.actionsCount > 0 && (
                  <div className="mt-2 flex items-center gap-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Icon name="users" variant="outline" size="xs" className="text-gray-400" />
                      {data.contactsCount} contacten
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="checkCircle" variant="outline" size="xs" className="text-gray-400" />
                      {data.completedActions}/{data.actionsCount} taken
                    </span>
                  </div>
                )}
              </div>

              {/* Arrow */}
              <Icon
                name="chevronRight"
                variant="outline"
                size="sm"
                className="text-gray-400 transition-transform group-hover:translate-x-1 group-hover:text-purple-600"
              />
            </div>
          )}

          {/* Helpful description for new users */}
          {!isLoading && !hasSetup && (
            <p className="mt-3 text-sm text-gray-600">
              Leg belangrijke contactgegevens en instructies vast voor je
              naasten. Dit helpt hen in een moeilijke tijd.
            </p>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}

export default NalatenschapDossierCard;
